import * as THREE from 'three';
import { settings } from '@/config';

/**
 * Height (altitude) fog — makes the scene geometry SIT IN the cloud sea instead of
 * floating as a hard cut-out against it. Every patched material fades its final colour
 * toward the cloud-haze colour the lower it is in world space: clear above `top`, fully
 * hazed at/below `bottom`. So the platform underside + the lower stage stones dissolve
 * into the same soft cloud tone as the `CloudSea` puffs behind them (the reference
 * "floating on a sea of clouds" look).
 *
 * Implemented as a shared-uniform `onBeforeCompile` patch (one shared uniform object →
 * every material), and it CHAINS over any existing `onBeforeCompile` (e.g. the intro
 * {@link RevealEffect}) rather than replacing it. The fade is applied at the very end of
 * the fragment shader, in the renderer's linear working space (the EffectComposer's
 * RenderPass renders untonemapped/linear), and the fog colour is a `THREE.Color` (stored
 * linear) so it matches the cloud colours, which are handled the same way.
 */
export class HeightFog {
  private readonly uniforms = {
    uHFColor: { value: new THREE.Color(settings.heightFog.color) },
    uHFBottom: { value: settings.heightFog.bottom },
    uHFTop: { value: settings.heightFog.top },
    uHFStrength: { value: settings.heightFog.strength },
  };
  private readonly patched = new Set<THREE.Material>();

  /** Patch every material under `root` so its fragments fade into the cloud haze. */
  apply(root: THREE.Object3D): void {
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) if (material) this.patch(material);
    });
  }

  private patch(material: THREE.Material): void {
    if (this.patched.has(material)) return;
    // Only the lit architecture materials use the standard chunk pipeline; skip raw
    // ShaderMaterials (e.g. the god-ray shafts) whose source has no `<dithering_fragment>`.
    const m = material as THREE.MeshStandardMaterial & THREE.MeshBasicMaterial;
    if (!m.isMeshStandardMaterial && !m.isMeshBasicMaterial) return;
    this.patched.add(material);

    // Preserve any existing patch (intro reveal) and run it first.
    const prev = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      if (prev) prev.call(material, shader, renderer);

      shader.uniforms.uHFColor = this.uniforms.uHFColor;
      shader.uniforms.uHFBottom = this.uniforms.uHFBottom;
      shader.uniforms.uHFTop = this.uniforms.uHFTop;
      shader.uniforms.uHFStrength = this.uniforms.uHFStrength;

      shader.vertexShader =
        'varying vec3 vHFWorld;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n  vHFWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;',
        );

      shader.fragmentShader =
        HF_HEAD +
        shader.fragmentShader.replace(
          '#include <dithering_fragment>',
          HF_BODY + '\n#include <dithering_fragment>',
        );
    };
    material.needsUpdate = true;
  }

  /** Push live settings to the shared uniforms (all patched materials update at once). */
  applySettings(): void {
    const cfg = settings.heightFog;
    (this.uniforms.uHFColor.value as THREE.Color).set(cfg.color);
    this.uniforms.uHFBottom.value = cfg.bottom;
    this.uniforms.uHFTop.value = cfg.top;
    this.uniforms.uHFStrength.value = cfg.strength;
  }

  dispose(): void {
    this.patched.clear();
  }
}

const HF_HEAD = /* glsl */ `
  uniform vec3 uHFColor;
  uniform float uHFBottom;
  uniform float uHFTop;
  uniform float uHFStrength;
  varying vec3 vHFWorld;
`;

const HF_BODY = /* glsl */ `
  // 1 at/below the bottom plane → 0 at/above the top plane.
  float hfFactor = (1.0 - smoothstep(uHFBottom, uHFTop, vHFWorld.y)) * uHFStrength;
  gl_FragColor.rgb = mix(gl_FragColor.rgb, uHFColor, clamp(hfFactor, 0.0, 1.0));
`;
